import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { paymentAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';
import { trackPricingView, trackInitiateCheckout, trackAddToCart } from '../../lib/metaPixel';
import {
    Check,
    Star,
    Zap,
    Crown,
    MapPin,
    Send,
    Bell,
    Award,
    Briefcase,
    FileText,
    MessageSquare,
    Clock,
    Users
} from 'lucide-react';

const PricingPage = () => {
    const { isAuthenticated, login } = useAuth();
    const navigate = useNavigate();

    // Track pricing page view on mount
    useEffect(() => {
        trackPricingView('subscription');
    }, []);

    // Check for saved pricing intent after login
    useEffect(() => {
        if (isAuthenticated) {
            const savedIntent = localStorage.getItem('pricingIntent');
            if (savedIntent) {
                try {
                    const intent = JSON.parse(savedIntent);
                    localStorage.removeItem('pricingIntent');
                    
                    // Small delay to ensure auth state is fully loaded
                    setTimeout(() => {
                        if (intent.type === 'subscription') {
                            handleSubscribeAfterLogin(intent.tier);
                        } else if (intent.type === 'multi_apply') {
                            handleMultiApplyAfterLogin(intent.packageId);
                        }
                    }, 500);
                } catch (e) {
                    console.error('Failed to parse pricing intent:', e);
                    localStorage.removeItem('pricingIntent');
                }
            }
        }
    }, [isAuthenticated]);

    const accessPlans = [
        {
            id: 'free',
            name: 'Free',
            price: 0,
            period: '/month',
            description: 'Explore recruiters before committing',
            icon: MapPin,
            color: 'neutral',
            features: [
                'Search recruiters near you',
                'View recruiter & agency profiles',
                'Filter by industry & location',
            ],
            limitations: [],
            cta: 'Find Recruiters Near Me',
            ctaLink: '/agencies',
        },
        {
            id: 'standard',
            name: 'Standard',
            price: 2.98,
            period: '/month',
            description: 'Connect directly with recruiters',
            icon: Send,
            color: 'primary',
            popular: true,
            features: [
                'Everything in Free',
                'Submit your CV to recruiters',
                'Unlock direct recruiter contact details',
                'Save & shortlist recruiters',
                'New recruiter alerts',
            ],
            cta: 'Unlock Recruiter Access',
        },
        {
            id: 'pro',
            name: 'Pro',
            price: 9.98,
            period: '/month',
            description: 'Maximum visibility. Faster responses.',
            icon: Crown,
            color: 'amber',
            features: [
                'Everything in Standard',
                'Priority CV visibility',
                'Featured candidate badge',
                'Early access to new recruiters',
                'Priority support',
            ],
            cta: 'Get Priority Access',
        },
    ];

    const multiApplyPackages = [
        {
            id: 'starter',
            name: 'Starter',
            monthlyPrice: 36,
            applications: 100,
            description: 'Ideal for focused job searching',
            icon: FileText,
            features: [
                'Up to 100 job applications per month',
                'Applications tailored to each role',
                'Basic CV improvement suggestions',
                'Email support',
            ],
            cta: 'Get Started',
        },
        {
            id: 'pro',
            name: 'Pro',
            monthlyPrice: 74,
            applications: 250,
            description: 'For faster interviews and stronger applications',
            icon: Zap,
            popular: true,
            features: [
                'Everything in Starter',
                'Up to 250 job applications per month',
                'Professionally optimised CV',
                'Custom cover letter for each role',
                'Supporting statement writing',
                'Priority support',
            ],
            cta: 'Get Started',
        },
        {
            id: 'pro_plus',
            name: 'Pro+',
            monthlyPrice: 148,
            applications: 250,
            description: 'Maximum exposure. Maximum support.',
            icon: Award,
            bestValue: true,
            features: [
                'Everything in Pro',
                'Premium CV optimisation & full review',
                'Tailored cover letters for every role',
                'Supporting statement writing',
                'Follow-up emails to hiring managers',
                'Dedicated account manager',
                '1-on-1 career coaching session',
            ],
            cta: 'Get Started',
        },
    ];

    const handleSubscribe = async (tier) => {
        // Free plan always navigates to agencies - no login required
        if (tier === 'free') {
            window.location.href = '/agencies';
            return;
        }

        if (!isAuthenticated) {
            // Save the purchase intent and redirect to login page
            localStorage.setItem('pricingIntent', JSON.stringify({
                type: 'subscription',
                tier: tier
            }));
            // Redirect to login page with return URL
            navigate('/login', { state: { from: { pathname: '/pricing' }, message: 'Please sign in to continue with your subscription' } });
            return;
        }

        // Find plan details for tracking
        const plan = accessPlans.find(p => p.id === tier);
        const price = plan?.price || 0;
        
        // Track checkout initiation
        trackAddToCart(`${tier} Access Plan`, price, 'subscription');
        trackInitiateCheckout(`${tier} Access Plan`, price, 'subscription');

        // Navigate to custom checkout page with order details
        navigate('/checkout', {
            state: {
                type: 'subscription',
                tier: tier,
                name: `${plan?.name || tier} Access Plan`,
                amount: Math.round(price * 100), // Convert to pence
                applications: null,
                metadata: {}
            }
        });
    };

    const handleMultiApply = async (packageId) => {
        if (!isAuthenticated) {
            // Save the purchase intent and redirect to login page
            localStorage.setItem('pricingIntent', JSON.stringify({
                type: 'multi_apply',
                packageId: packageId
            }));
            // Redirect to login page with return URL
            navigate('/login', { state: { from: { pathname: '/pricing' }, message: 'Please sign in to continue with your purchase' } });
            return;
        }

        // Find package details for tracking
        const pkg = multiApplyPackages.find(p => p.id === packageId);
        
        // Get the correct price based on package type
        let price = 0;
        if (pkg) {
            price = pkg.monthlyPrice || 0;
        }
        
        // Track checkout initiation
        trackAddToCart(`Multi-Apply ${packageId}`, price, 'multi_apply');
        trackInitiateCheckout(`Multi-Apply ${packageId}`, price, 'multi_apply');

        // Navigate to custom checkout page with order details
        navigate('/checkout', {
            state: {
                type: 'multi_apply',
                packageId: packageId,
                name: `Multi-Apply ${pkg?.name || packageId} Package`,
                amount: Math.round(price * 100), // Convert to pence
                applications: pkg?.applications || 50,
                billingPeriod: 'monthly',
                metadata: {}
            }
        });
    };

    // Helper function to proceed to checkout after login (for subscription)
    const handleSubscribeAfterLogin = (tier) => {
        if (tier === 'free') {
            navigate('/agencies');
            return;
        }

        const plan = accessPlans.find(p => p.id === tier);
        const price = plan?.price || 0;
        
        trackAddToCart(`${tier} Access Plan`, price, 'subscription');
        trackInitiateCheckout(`${tier} Access Plan`, price, 'subscription');

        navigate('/checkout', {
            state: {
                type: 'subscription',
                tier: tier,
                name: `${plan?.name || tier} Access Plan`,
                amount: Math.round(price * 100),
                applications: null,
                metadata: {}
            }
        });
    };

    // Helper function to proceed to checkout after login (for multi-apply)
    const handleMultiApplyAfterLogin = (packageId) => {
        const pkg = multiApplyPackages.find(p => p.id === packageId);
        
        let price = 0;
        if (pkg) {
            price = pkg.monthlyPrice || 0;
        }
        
        trackAddToCart(`Multi-Apply ${packageId}`, price, 'multi_apply');
        trackInitiateCheckout(`Multi-Apply ${packageId}`, price, 'multi_apply');

        navigate('/checkout', {
            state: {
                type: 'multi_apply',
                packageId: packageId,
                name: `Multi-Apply ${pkg?.name || packageId} Package`,
                amount: Math.round(price * 100),
                applications: pkg?.applications || 50,
                billingPeriod: 'monthly',
                metadata: {}
            }
        });
    };

    return (
        <Layout>
            {/* Hero Section */}
            <section className="bg-gradient-to-br from-primary-950 via-primary-900 to-neutral-900 text-white py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 mb-6">
                        <Zap className="w-3 h-3 mr-1" />
                        Free to Search • Only Multi-Apply is Paid
                    </Badge>
                    <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
                        Invest in Your Career.<br />See Results in Weeks.
                    </h1>
                    <p className="text-lg text-neutral-300 max-w-2xl mx-auto">
                        Stop wasting months on job boards. Finding recruiters and agency jobs is free. Only Multi-Apply, where our team applies for you, is paid.
                    </p>
                </div>
            </section>

            {/* Free Access Banner */}
            <section className="py-14 bg-neutral-50" data-testid="free-access-banner">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="rounded-2xl border border-emerald-200 bg-white p-8 flex flex-col md:flex-row md:items-center gap-6">
                        <div className="flex-1">
                            <Badge className="bg-emerald-100 text-emerald-700 mb-3">FREE FOR EVERYONE</Badge>
                            <h2 className="font-heading text-2xl font-bold text-neutral-900 mb-2">Find Recruiters & Agency Jobs are free</h2>
                            <p className="text-neutral-600">Create a free account to see every agency's contact details and apply to thousands of jobs, with no card and no subscription.</p>
                        </div>
                        <Button className="bg-emerald-600 hover:bg-emerald-700 h-12 px-6" onClick={() => navigate(isAuthenticated ? '/agencies' : '/login')} data-testid="free-access-cta">
                            {isAuthenticated ? 'Find Recruiters' : 'Sign Up Free'}
                        </Button>
                    </div>
                </div>
            </section>

            {/* Multi-Apply Section */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <Badge className="bg-amber-100 text-amber-700 mb-4">
                            <Zap className="w-3 h-3 mr-1" />
                            OUR #1 SERVICE — 95% SUCCESS RATE
                        </Badge>
                        <h2 className="font-heading text-3xl font-bold text-neutral-900 mb-2">
                            Multi-Apply: We Apply, You Interview
                        </h2>
                        <p className="text-neutral-600 max-w-2xl mx-auto">
                            Stop spending 40+ hours a week on applications. Our expert team submits tailored, 
                            high-quality applications while you focus on interview prep.
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-6 mt-6">
                            <div className="flex items-center gap-2 text-sm text-neutral-600">
                                <Check className="w-4 h-4 text-emerald-500" />
                                <span>Human-written applications</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-neutral-600">
                                <Check className="w-4 h-4 text-emerald-500" />
                                <span>Tailored to each role</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-neutral-600">
                                <Check className="w-4 h-4 text-emerald-500" />
                                <span>Real-time dashboard tracking</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {multiApplyPackages.map((pkg) => (
                            <div
                                key={pkg.id}
                                className={`relative bg-white rounded-2xl p-8 border-2 ${
                                    pkg.popular ? 'border-primary-600' : pkg.bestValue ? 'border-emerald-500' : 'border-neutral-200'
                                } hover:shadow-xl transition-all`}
                                data-testid={`package-${pkg.id}`}
                            >
                                {pkg.popular && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <Badge className="bg-primary-600 text-white">
                                            <Zap className="w-3 h-3 mr-1" />
                                            Most Popular
                                        </Badge>
                                    </div>
                                )}
                                {pkg.bestValue && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <Badge className="bg-emerald-600 text-white">
                                            <Award className="w-3 h-3 mr-1" />
                                            Best Value for Long-Term Search
                                        </Badge>
                                    </div>
                                )}

                                <div className="w-14 h-14 rounded-xl bg-primary-100 flex items-center justify-center mb-6">
                                    <pkg.icon className="w-7 h-7 text-primary-600" />
                                </div>

                                <h3 className="font-heading text-xl font-semibold text-neutral-900">
                                    {pkg.name}
                                </h3>
                                <p className="text-sm text-neutral-500 mt-1">{pkg.description}</p>

                                {/* Pricing */}
                                <div className="mt-6">
                                    <div className="flex items-baseline gap-1">
                                        <span className="font-heading text-4xl font-bold text-neutral-900">
                                            £{pkg.monthlyPrice}
                                        </span>
                                        <span className="text-neutral-500">/month</span>
                                    </div>
                                </div>

                                {/* Applications Summary */}
                                <div className="mt-4 p-3 bg-primary-50 rounded-lg">
                                    <div className="flex items-center justify-center text-sm">
                                        <span className="font-medium text-primary-900">
                                            Up to {pkg.applications} applications/month
                                        </span>
                                    </div>
                                </div>

                                <ul className="mt-6 space-y-3">
                                    {pkg.features.map((feature, idx) => (
                                        <li key={idx} className="flex items-start gap-3 text-sm">
                                            <Check className="w-5 h-5 text-emerald-500 shrink-0" />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <Button
                                    className={`w-full mt-8 h-12 ${
                                        pkg.popular ? 'bg-primary-600 hover:bg-primary-700 btn-glow' :
                                        pkg.bestValue ? 'bg-emerald-600 hover:bg-emerald-700' :
                                        'bg-neutral-900 hover:bg-neutral-800'
                                    }`}
                                    onClick={() => handleMultiApply(pkg.id)}
                                    data-testid={`buy-${pkg.id}-btn`}
                                >
                                    {pkg.cta}
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20 bg-neutral-50">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="font-heading text-3xl font-bold text-neutral-900 text-center mb-12">
                        Frequently Asked Questions
                    </h2>

                    <div className="space-y-6">
                        {[
                            {
                                q: 'Is Find Recruiters really free?',
                                a: 'Yes. Find Recruiters, Agency Jobs and recruiter contact details are free for every registered user. Only Multi-Apply is a paid service.',
                            },
                            {
                                q: 'How does Multi-Apply work?',
                                a: 'Once you subscribe to a package, our team reviews your CV and job preferences. We then find suitable jobs and apply on your behalf with tailored applications, keeping you updated through your dashboard.',
                            },
                            {
                                q: 'Can I cancel my subscription?',
                                a: 'Yes, you can cancel your subscription at any time. Your access will remain until the end of your billing period.',
                            },
                            {
                                q: 'What\'s the difference between Multi-Apply packages?',
                                a: 'Starter gives you up to 100 applications/month with basic CV tips. Pro adds 250 applications, professional CV optimisation, and custom cover letters. Pro+ includes everything in Pro plus follow-up emails, a dedicated account manager, and career coaching.',
                            },
                        ].map((faq, idx) => (
                            <div key={idx} className="bg-white rounded-xl p-6">
                                <h3 className="font-semibold text-neutral-900">{faq.q}</h3>
                                <p className="text-neutral-600 mt-2 text-sm">{faq.a}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default PricingPage;
